import { createSlice } from '@reduxjs/toolkit'
import { loadFormData } from 'apps/builder/store/actions/form'
import { importFormData } from 'apps/builder/store/actions/importData'
import {
  copySheet,
  copyTableOrSimpleLoopSheet,
  convertToLoopScreen,
  convertToTableScreen,
  convertScreenToList,
  deleteSheet,
  promoteToLoopScreen,
  promoteToTableScreen,
  splitSheet,
  updateSheetSection,
} from 'apps/builder/store/actions/sheets'
import { deleteEntry, updateEntry, copyEntry, updateIndex } from 'apps/builder/store/actions/entries'
import { saveApp } from 'apps/formsCreationKit/store/reducers/settings/appSettings'
import { guid, isTrueStringMatch, whiteListedTypesForCalculation } from 'apps/builder/lib/utils'
import { deterministicGuid } from 'apps/builder/lib/hash'

const adjustIndexes = (operations) => {
  // Use a Map to track the next expected index for each entry
  const nextExpectedIndex = new Map()
  // Sort operations by index
  return operations
    .sort((a, b) => a.index - b.index)
    .map((operation) => {
      const { entry } = operation
      // Initialize the next expected index for this entry if not already set
      if (!nextExpectedIndex.has(entry)) {
        nextExpectedIndex.set(entry, 0)
      }
      // Get the current expected index for this entry
      const expectedIndex = nextExpectedIndex.get(entry)
      // Update the next expected index for this entry
      nextExpectedIndex.set(entry, expectedIndex + 1)
      // Return the operation with the adjusted index
      return {
        ...operation,
        index: expectedIndex, // Assign the current expected index
      }
    })
}
// operations that aren't on the same sheet (except what will be the immediate keyfield)
// should break unless the calculation field is on the looped sheet &
// can still reach the parentEntry
const handleConvertToLoopOrTable = (operations, action) => {
  const { id: oldSheetId, newSheetId, keyField = {} } = action.payload
  const updatedOperations = operations.reduce((acc, operation) => {
    const {
      parentEntryGuid,
      sheet_id,
      parentEntrySheetId,
    } = operation
    const { guid: keyFieldGuid } = keyField
    let updatedOperation = { ...operation }
    // either field in the operation is on the affected sheet
    const isAffected = isTrueStringMatch(sheet_id, oldSheetId) || isTrueStringMatch(parentEntrySheetId, oldSheetId)
    // calculation field is not on the affected sheet
    const calcIsExternal = !isTrueStringMatch(sheet_id, oldSheetId)
    if (isAffected && calcIsExternal) {
      // calulation entry isn't on the looped sheet, so drop the operation
      return acc
    }
    // TODO: need to remove cases where the calculation field points at a top-level keyfield
    // Handle when the parentEntry becomes the keyField case
    // it should end up on the "new" sheet but is still a valid target
    if (parentEntryGuid === keyFieldGuid) {
      // Update the parentEntrySheetId if keyField matches parentEntryGuid
      updatedOperation.parentEntrySheetId = newSheetId
    }
    acc.push(updatedOperation)
    return acc
  }, [])
  return adjustIndexes(updatedOperations)
}

const copyOperations = (
  operations,
  newSheetIdLookup,
  copiedSheetEntries,
  seed
) => {
  const copiedCalculationEntryGuids = copiedSheetEntries.reduce(
    (acc, entry) => {
      if (entry.type === 'Calculation') acc.push(entry.guid)
      return acc
    },
    []
  )

  if (copiedCalculationEntryGuids.length === 0) return []

  return operations.reduce((acc, operation) => {
    const {
      parentEntryGuid,
      parentEntrySheetId,
      entry: entryGuid,
      sheet_id: entrySheetId,
    } = operation
    if (copiedCalculationEntryGuids.includes(entryGuid)) {
      const updatedOperation = {
        ...operation,
        id: guid(),
        sheet_id: newSheetIdLookup[entrySheetId],
        entry: deterministicGuid(entryGuid, seed),
      }

      // If the operand is on the copied sheet, we need to update it to point to the new sheet/entry copy
      const needsParentEntryUpdate = Object.keys(newSheetIdLookup).find(
        copiedSheetId => isTrueStringMatch(copiedSheetId, parentEntrySheetId)
      )
      if (needsParentEntryUpdate) {
        updatedOperation.parentEntrySheetId =
          newSheetIdLookup[parentEntrySheetId]
        updatedOperation.parentEntryGuid = deterministicGuid(
          parentEntryGuid,
          seed
        )
      }

      acc.push(updatedOperation)
    }

    return acc
  }, [])
}

export const deleteOperations = (operations, deletedEntryGuid) =>
  operations.filter(
    ({ entry: entryGuid, parentEntryGuid }) =>
      entryGuid != deletedEntryGuid && parentEntryGuid != deletedEntryGuid
  )

export const operationsReducerConfig = {
  name: 'operations',
  initialState: [],
  reducers: {
    addOperation(state, action) {
      state.push(action.payload)
    },
    updateOperation: (state, action) => {
      const { id } = action.payload
      return state.map(entry => {
        if (id !== entry.id) return entry
        return {
          ...entry,
          ...action.payload,
        }
      })
    },
    deleteOperation(state, action) {
      const id = action.payload
      return state.filter(entry => entry.id != id)
    },
  },
  extraReducers: {
    [loadFormData]: (state, action) => {
      const { operations } = action.payload
      return operations
    },
    [importFormData]: (state, action) => {
      const { operations = [] } = action.payload
      return [...state, ...operations]
    },
    [saveApp.fulfilled]: (state, action) => {
      const { entities } = action.payload
      const { operations = [] } = entities
      return operations
    },
    [updateIndex]: (state, action) => {
      const {
        guid: movedGuid,
        oldSheet,
        sheet: sheet_id,
        reachableSheets,
      } = action.payload
      // the moved entry stayed on the same sheet, so no changes needed
      if (isTrueStringMatch(sheet_id, oldSheet)) {
        return state
      }

      const { reachableBy, canReach } = reachableSheets

      // if any entry's sheet has changed, we need to update the sheet
      const updatedState = state.reduce((acc, operation) => {
        const {
          parentEntryGuid,
          entry: entryGuid,
          sheet_id: oldEntrySheetId,
          parentEntrySheetId: oldParentEntrySheetId,
        } = operation
        if (entryGuid !== movedGuid && parentEntryGuid !== movedGuid) {
          // no entries involved in this operation were moved, so no changes needed
          acc.push(operation)
          return acc
        }

        if (entryGuid === movedGuid && canReach.includes(oldParentEntrySheetId)) {
          // entry has moved, operation can remain only if sheet_id can reach the oldParentEntrySheetId
          acc.push({
            ...operation,
            sheet_id,
          })
        } else if (
          parentEntryGuid && parentEntryGuid === movedGuid && reachableBy.includes(oldEntrySheetId)
        ) {
          // parent entry has moved, operation can remain only if oldSheetId can reach the sheet_id
          acc.push({
            ...operation,
            parentEntrySheetId: sheet_id,
          })
        }
        return acc
      }, [])
      return adjustIndexes(updatedState)
    },
    [updateSheetSection]: (state, action) => {
      const { id, childSheets = [], standardPrimarySheets = [] } = action.payload
      const affectedSheets = [String(id), ...childSheets.map(({ id }) => String(id))]
      const topLevelNormalSheets = standardPrimarySheets.map(({ id }) => String(id))

      const updatedState = state.reduce((acc, operation) => {
        const { sheet_id, parentEntrySheetId } = operation
        // operation doesn't involve the affected sheets, keep
        if (!affectedSheets.includes(String(sheet_id)) && !affectedSheets.includes(String(parentEntrySheetId))) {
          acc.push(operation)
          return acc
        }
        if (
          affectedSheets.includes(String(sheet_id)) &&
          affectedSheets.includes(String(parentEntrySheetId))
        ) {
          // operation is _entirely_ on the affected sheets, keep
          acc.push(operation)
          return acc
        }
        if (
          affectedSheets.includes(String(sheet_id)) &&
          !affectedSheets.includes(String(parentEntrySheetId)) &&
          topLevelNormalSheets.includes(String(parentEntrySheetId))
        ) {
          // calculation field is on an affected sheet, but the parent entry is on a top-level normal sheet, keep
          acc.push(operation)
          return acc
        }
        // TODO, retain any other operations that are still valid
        // would likely include moving screen up or down in the same loop hierarchy
        return acc
      }, [])
      
      return adjustIndexes(updatedState)
    },
    [deleteSheet]: (state, action) => {
      const { id, additionalSheets = [] } = action.payload
      const sheetsToDelete = [id, ...additionalSheets.map(({ id }) => id)]
      const updatedState = state.filter(operation => {
        const { sheet_id, parentEntrySheetId } = operation
        if (
          sheetsToDelete.includes(sheet_id) || sheetsToDelete.includes(parentEntrySheetId)
        ) {
          return false
        }
        return true
      })
      return adjustIndexes(updatedState)
    },
    [convertToLoopScreen]: handleConvertToLoopOrTable,
    [convertToTableScreen]: handleConvertToLoopOrTable,
    [promoteToLoopScreen]: handleConvertToLoopOrTable,
    [promoteToTableScreen]: handleConvertToLoopOrTable,
    [convertScreenToList]: (state, action) => {
      const { id } = action.payload
      // remove any operations referring to value of the keyfield on the list
      return adjustIndexes(
        state.filter(({ parentEntrySheetId }) => parentEntrySheetId !== id)
      )
    },
    [copyEntry]: (state, action) => {
      const { guid: originalGuid, seed } = action.payload
      const newOperations = state.reduce((acc, operation) => {
        const { entry: entryGuid } = operation
        if (isTrueStringMatch(entryGuid, originalGuid)) {
          acc.push({
            ...operation,
            id: guid(),
            entry: deterministicGuid(entryGuid, seed),
          })
        }
        return acc
      }, [])
      return [...state, ...newOperations]
    },
    [updateEntry]: (state, action) => {
      const { guid, type } = action.payload
      if (!type) {
        // only need to respond to type updates
        return state
      }
      return adjustIndexes(
        state.filter(({ parentEntryGuid, entry: entryGuid }) => {
          const isAffected = guid === entryGuid || guid === parentEntryGuid
          const isValid =
            (guid === entryGuid && type === 'Calculation') ||
            (guid === parentEntryGuid && whiteListedTypesForCalculation.includes(type))
          return !isAffected || isValid
        })
      )
    },
    [deleteEntry]: (state, action) => {
      const { guid: deletedEntryGuid } = action.payload
      return adjustIndexes(deleteOperations(state, deletedEntryGuid))
    },
    [copySheet]: (state, action) => {
      const {
        id: copiedSheetId,
        newId,
        sheetEntries = [],
        seed,
      } = action.payload

      const copiedOperations = copyOperations(
        state,
        { [copiedSheetId]: newId },
        sheetEntries,
        seed
      )

      return [...state, ...copiedOperations]
    },
    [copyTableOrSimpleLoopSheet]: (state, action) => {
      const {
        parentSheetId,
        sheetsToCopy = [],
        entriesToCopy = [],
        newSheetGuid,
        newMultiSheetGuid,
        seed,
      } = action.payload

      const childSheetId = sheetsToCopy.find(
        id => !isTrueStringMatch(parentSheetId, id)
      )

      const newSheetIdLookup = {
        [parentSheetId]: newSheetGuid,
        [childSheetId]: newMultiSheetGuid,
      }

      const copiedOperations = copyOperations(
        state,
        newSheetIdLookup,
        entriesToCopy,
        seed
      )

      return [...state, ...copiedOperations]
    },
    [splitSheet]: (state, action) => {
      const { newId, sheetEntries, position } = action.payload

      const entriesSplitToNewSheet = sheetEntries.reduce((acc, entry) => {
        if (entry.position > position) acc.push(entry.guid)
        return acc
      }, [])

      return state.map(operation => {
        const { parentEntryGuid, entry: entryGuid } = operation
        const updatedOperation = { ...operation }

        if (entriesSplitToNewSheet.includes(parentEntryGuid)) {
          updatedOperation.parentEntrySheetId = newId
        }
        if (entriesSplitToNewSheet.includes(entryGuid)) {
          updatedOperation.sheet_id = newId
        }

        return updatedOperation
      })
    },
  },
}

export const operationSlice = createSlice(operationsReducerConfig)

const { actions, reducer } = operationSlice

export const operationActions = actions
export const operationReducer = reducer
